import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  SyncOutlined,
  CloudDoneOutlined,
  CloudOffOutlined,
  SaveOutlined,
  DeleteOutlined,
  Visibility,
  VisibilityOff,
  RefreshOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { getToken, base_url } from '../../config/config';
import axios from 'axios';

const OdooConfig = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configExists, setConfigExists] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [syncStatus, setSyncStatus] = useState({ lastSync: null, inProgress: false });
  const [configData, setConfigData] = useState({
    name: 'Default Configuration',
    host: '',
    port: 8069,
    database: '',
    username: '',
    password: '',
    isActive: true,
    syncInterval: 60, // in minutes
  });

 
  useEffect(() => {
    
    fetchOdooConfig();
  }, []);

  const fetchOdooConfig = async () => {
    setLoading(true);
    try {
      const token = getToken();
      console.log(token)
      const response = await axios.get(`${base_url}/v1/stock/odoo/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setConfigData(response.data.data);
        setConfigExists(true);
        setSyncStatus({
          lastSync: response.data.data.lastSync,
          inProgress: false
        });
      } else {
        setConfigExists(false);
      }
    } catch (error) {
      console.error('Error fetching Odoo configuration:', error);
      setConfigExists(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === "isActive") {
      setConfigData({
        ...configData,
        [name]: checked
      });
    } else if (name === "port" || name === "syncInterval") {
      setConfigData({
        ...configData,
        [name]: parseInt(value) || 0
      });
    } else {
      setConfigData({
        ...configData,
        [name]: value
      });
    }
  };

  const handleSaveConfig = async () => {
    // Validate required fields
    const requiredFields = ['host', 'port', 'database', 'username', 'password'];
    const missingFields = requiredFields.filter(field => !configData[field]);
    
    if (missingFields.length > 0) {
      setAlert({
        open: true,
        message: `Please fill in all required fields: ${missingFields.join(', ')}`,
        severity: 'error'
      });
      return;
    }
    
    setSaving(true);
    try {
      const token = getToken();
      const response = await axios.post(`${base_url}/v1/stock/odoo/config`, configData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setConfigData(response.data.data);
        setConfigExists(true);
        setAlert({
          open: true,
          message: 'Odoo configuration saved successfully',
          severity: 'success'
        });
      } else {
        setAlert({
          open: true,
          message: response.data.message || 'Failed to save configuration',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving Odoo configuration:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Failed to save configuration',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    // Validate required fields
    const requiredFields = ['host', 'port', 'database', 'username', 'password'];
    const missingFields = requiredFields.filter(field => !configData[field]);
    
    if (missingFields.length > 0) {
      setAlert({
        open: true,
        message: `Please fill in all required fields: ${missingFields.join(', ')}`,
        severity: 'error'
      });
      return;
    }
    
    setTesting(true);
    try {
      const token = getToken();
      const response = await axios.post(`${base_url}/v1/stock/odoo/config`, 
        { ...configData, testOnly: true }, // Add testOnly flag
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAlert({
          open: true,
          message: 'Connection successful! Odoo connection test passed.',
          severity: 'success'
        });
      } else {
        setAlert({
          open: true,
          message: response.data.message || 'Connection test failed',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error testing Odoo connection:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Connection test failed',
        severity: 'error'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteConfig = async () => {
    setConfirmDialog(false);
    try {
      const token = getToken();
      const response = await axios.delete(`${base_url}/v1/stock/odoo/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setConfigData({
          name: 'Default Configuration',
          host: '',
          port: 8069,
          database: '',
          username: '',
          password: '',
          isActive: true,
          syncInterval: 60,
        });
        setConfigExists(false);
        setAlert({
          open: true,
          message: 'Odoo configuration deleted successfully',
          severity: 'success'
        });
      } else {
        setAlert({
          open: true,
          message: response.data.message || 'Failed to delete configuration',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting Odoo configuration:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Failed to delete configuration',
        severity: 'error'
      });
    }
  };

  const handleSyncWithOdoo = async () => {
    setSyncStatus({ ...syncStatus, inProgress: true });
    try {
      const token = getToken();
      const response = await axios.post(`${base_url}/v1/stock/odoo/sync`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAlert({
          open: true,
          message: 'Successfully synchronized with Odoo',
          severity: 'success'
        });
        // Update last sync time
        setSyncStatus({
          lastSync: new Date(),
          inProgress: false
        });
      } else {
        setAlert({
          open: true,
          message: response.data.message || 'Synchronization failed',
          severity: 'error'
        });
        setSyncStatus({ ...syncStatus, inProgress: false });
      }
    } catch (error) {
      console.error('Error syncing with Odoo:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Synchronization failed',
        severity: 'error'
      });
      setSyncStatus({ ...syncStatus, inProgress: false });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" mb="5px">
            Odoo Configuration
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Configure Odoo ERP Integration
          </Typography>
        </Box>
        
        {configExists && (
          <Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={syncStatus.inProgress ? <CircularProgress size={20} color="inherit" /> : <SyncOutlined />}
              onClick={handleSyncWithOdoo}
              disabled={syncStatus.inProgress || !configData.isActive}
              sx={{ mr: 1 }}
            >
              Sync with Odoo
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlined />}
              onClick={() => setConfirmDialog(true)}
            >
              Delete Configuration
            </Button>
          </Box>
        )}
      </Box>

      {/* CONNECTION STATUS CARD */}
      {configExists && (
        <Card
          sx={{
            mt: 2,
            mb: 3,
            bgcolor: configData.isActive ? colors.greenAccent[700] : colors.grey[700],
            borderRadius: '8px',
            boxShadow: 3,
          }}
        >
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                {configData.isActive ? (
                  <CloudDoneOutlined fontSize="large" />
                ) : (
                  <CloudOffOutlined fontSize="large" />
                )}
              </Grid>
              <Grid item xs>
                <Typography variant="h4" component="div">
                  {configData.isActive ? 'Odoo Connection Active' : 'Odoo Connection Inactive'}
                </Typography>
                <Typography variant="body1" color={colors.grey[300]}>
                  {configData.name} • {configData.host}:{configData.port} • Database: {configData.database}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color={colors.grey[400]}>
                  Last Sync: {syncStatus.lastSync 
                    ? new Date(syncStatus.lastSync).toLocaleString() 
                    : 'Never'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* CONFIGURATION FORM */}
      <Paper sx={{ p: 3, bgcolor: colors.primary[400], borderRadius: '8px' }}>
        <Typography variant="h4" mb={3}>
          {configExists ? 'Edit Configuration' : 'New Configuration'}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Configuration Name"
              name="name"
              value={configData.name}
              onChange={handleConfigChange}
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Odoo Host"
              name="host"
              value={configData.host}
              onChange={handleConfigChange}
              placeholder="e.g., https://your-instance.odoo.com"
              required
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Port"
              name="port"
              type="number"
              value={configData.port}
              onChange={handleConfigChange}
              required
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Database Name"
              name="database"
              value={configData.database}
              onChange={handleConfigChange}
              required
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Username"
              name="username"
              value={configData.username}
              onChange={handleConfigChange}
              required
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
            />
            
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={configData.password}
              onChange={handleConfigChange}
              required
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              label="Sync Interval (minutes)"
              name="syncInterval"
              type="number"
              value={configData.syncInterval}
              onChange={handleConfigChange}
              fullWidth
              variant="filled"
              sx={{ mb: 2 }}
              InputProps={{
                inputProps: { min: 5 }
              }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={configData.isActive}
                  onChange={handleConfigChange}
                  name="isActive"
                  color="secondary"
                />
              }
              label="Active Connection"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleTestConnection}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={20} /> : <RefreshOutlined />}
            sx={{ mr: 2 }}
          >
            Test Connection
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveConfig}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveOutlined />}
          >
            Save Configuration
          </Button>
        </Box>
      </Paper>

      {/* INSTRUCTIONS SECTION */}
      <Paper sx={{ mt: 3, p: 3, bgcolor: colors.primary[400], borderRadius: '8px' }}>
        <Typography variant="h4" mb={2}>
          Setup Instructions
        </Typography>
        <Typography variant="body1" paragraph>
          To connect with Odoo ERP, you'll need your Odoo instance details:
        </Typography>
        <ol>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Host:</strong> The URL of your Odoo instance (e.g., https://your-instance.odoo.com)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Port:</strong> Usually 8069 for HTTP or 443 for HTTPS
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Database Name:</strong> Your Odoo database name
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Username & Password:</strong> Credentials with API access permissions
            </Typography>
          </li>
        </ol>
        <Typography variant="body1" paragraph>
          After configuration, use the "Sync with Odoo" button to synchronize products and inventory data between systems.
        </Typography>
      </Paper>

      {/* CONFIRM DELETE DIALOG */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Delete Odoo Configuration</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the Odoo configuration? This will remove the connection settings and disable synchronization.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfig} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OdooConfig;